import { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { CheckCircle, XCircle, Clock } from "react-bootstrap-icons";
import astronautImg from "../assets/img/Astronaut.webp";
import { trackContactFormSubmit } from '../lib/analytics';
import { usePrefersReducedMotion } from '../lib/usePrefersReducedMotion';
import 'animate.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Contact = () => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const formInitialDetails = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    servicePackage: '',
    message: '',
    website: '' // honeypot — real users leave this blank
  }

  const [formDetails, setFormDetails] = useState(formInitialDetails);
  const [formState, setFormState] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [statusMessage, setStatusMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Check for pre-selected tier from pricing page
  useEffect(() => {
    const selectedTier = sessionStorage.getItem('selectedTier');
    if (selectedTier) {
      setFormDetails(prev => ({
        ...prev,
        servicePackage: selectedTier
      }));
      // Clear the session storage after retrieving
      sessionStorage.removeItem('selectedTier');
    }
  }, []);

  const onFormUpdate = (category, value) => {
    setFormDetails({
      ...formDetails,
      [category]: value
    })
    if (fieldErrors[category]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[category];
        return next;
      });
    }
  }

  const validate = (details) => {
    const errors = {};
    if (!details.email.trim()) {
      errors.email = 'Email address is required.';
    } else if (!EMAIL_RE.test(details.email.trim())) {
      errors.email = 'Enter a valid email address, for example name@domain.com.';
    }
    if (!details.message.trim()) {
      errors.message = 'Message is required.';
    } else if (details.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters.';
    }
    return errors;
  }

  // Auto-restore form after success
  useEffect(() => {
    if (formState === 'success') {
      const timer = setTimeout(() => {
        setFormState('idle');
        setStatusMessage('');
      }, 4000); // Show success for 4 seconds

      return () => clearTimeout(timer);
    }
  }, [formState]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    const errors = validate(formDetails);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormState('error');
      setStatusMessage('Please fix the highlighted fields and try again.');
      const firstErrorField = Object.keys(errors)[0];
      const el = document.getElementById(firstErrorField);
      if (el) el.focus();
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    setFormState('loading');
    setStatusMessage('');

    // Track form submission attempt immediately
    trackContactFormSubmit();
    
    try {
      let response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json;charset=utf-8",
        },
        body: JSON.stringify(formDetails),
      });
      
      let result = await response.json();
      
      if (result.code === 200) {
        setFormState('success');
        setStatusMessage(result.message);
        setFormDetails(formInitialDetails); // Reset form on success
      } else {
        setFormState('error');
        setStatusMessage(result.message);
      }
    } catch (error) {
      setFormState('error');
      setStatusMessage('Network error. Please check your connection and try again.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getButtonContent = () => {
    switch (formState) {
      case 'loading':
        return (
          <>
            <Clock className="form-button-icon spinning" />
            <span>Sending...</span>
          </>
        );
      case 'success':
        return (
          <>
            <CheckCircle className="form-button-icon" />
            <span>Sent!</span>
          </>
        );
      case 'error':
        return <span>Try Again</span>;
      default:
        return <span>Send Message</span>;
    }
  };

  const getStatusContent = () => {
    if (!statusMessage) return null;

    switch (formState) {
      case 'success':
        return (
          <div className="form-status-container success-state">
            <CheckCircle className="status-icon success-icon" />
            <div className="status-content">
              <h4 className="status-title">Message Sent Successfully!</h4>
              <p className="status-message">{statusMessage}</p>
            </div>
          </div>
        );
      case 'error':
        return (
          <div className="form-status-container error-state">
            <XCircle className="status-icon error-icon" />
            <div className="status-content">
              <h4 className="status-title">Message Failed to Send</h4>
              <p className="status-message">{statusMessage}</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const isFormDisabled = formState === 'loading' || formState === 'success';

  return (
    <section className="contact" id="connect" aria-labelledby="contact-heading">
      <Container>
        <Row className="align-items-center">
          <Col xs={12} md={6}>
            <img
              className="contact-astronaut"
              src={astronautImg}
              alt="Astronaut illustration on a frontier horizon"
            />
          </Col>
          <Col xs={12} md={6}>
            {/* Critical CTA — render unconditionally. Animation runs on mount
                so it completes well before the user scrolls down to the section,
                and the form is never dependent on TrackVisibility's scroll
                detection (which has been observed to fail on iOS Safari when
                combined with the section's overflow: hidden). */}
            <div className={prefersReducedMotion ? "" : "animate__animated animate__fadeIn"}>
                  <h2 id="contact-heading">Let's Find Your Gaps</h2>
                  <p className="contact-subheading">
                    Before we talk about what we'd build, let's look at exactly what your digital presence is costing you right now. Ask any questions necessary before you start.
                  </p>

                  <form
                    onSubmit={handleSubmit} 
                    noValidate 
                    aria-label="Contact form"
                    className={`contact-form ${formState}`}
                  >
                    <Row>
                      {/* Honeypot: hidden from users, bots fill it. Server rejects submissions where this is non-empty. */}
                      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                        <label htmlFor="website">Website (leave blank)</label>
                        <input
                          id="website"
                          type="text"
                          name="website"
                          tabIndex={-1}
                          autoComplete="off"
                          value={formDetails.website}
                          onChange={(e) => onFormUpdate('website', e.target.value)}
                        />
                      </div>
                      <Col xs={12} sm={6} className="px-1">
                        <label htmlFor="firstName" className="form-label">First Name</label>
                        <input
                          id="firstName"
                          type="text"
                          value={formDetails.firstName}
                          onChange={(e) => onFormUpdate('firstName', e.target.value)}
                          aria-required="false"
                          disabled={isFormDisabled}
                          className={`form-input ${formState}`}
                        />
                      </Col>
                      <Col xs={12} sm={6} className="px-1">
                        <label htmlFor="lastName" className="form-label">Last Name</label>
                        <input
                          id="lastName"
                          type="text"
                          value={formDetails.lastName}
                          onChange={(e) => onFormUpdate('lastName', e.target.value)}
                          aria-required="false"
                          disabled={isFormDisabled}
                          className={`form-input ${formState}`}
                        />
                      </Col>
                      <Col xs={12} sm={6} className="px-1">
                        <label htmlFor="email" className="form-label">
                          Email Address<span aria-hidden="true" className="required-marker">*</span>
                        </label>
                        <input
                          id="email"
                          type="email"
                          value={formDetails.email}
                          onChange={(e) => onFormUpdate('email', e.target.value)}
                          aria-required="true"
                          aria-invalid={!!fieldErrors.email}
                          aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                          required
                          disabled={isFormDisabled}
                          className={`form-input ${formState} ${fieldErrors.email ? 'invalid' : ''}`}
                        />
                        {fieldErrors.email && (
                          <span id="email-error" role="alert" className="field-error">
                            {fieldErrors.email}
                          </span>
                        )}
                      </Col>
                      <Col xs={12} sm={6} className="px-1">
                        <label htmlFor="phone" className="form-label">Phone Number</label>
                        <input
                          id="phone"
                          type="tel"
                          inputMode="tel"
                          value={formDetails.phone}
                          onChange={(e) => onFormUpdate('phone', e.target.value)}
                          aria-required="false"
                          disabled={isFormDisabled}
                          className={`form-input ${formState}`}
                        />
                      </Col>
                      <Col xs={12} className="px-1">
                        <label htmlFor="servicePackage" className="form-label">Service Package</label>
                        <select
                          id="servicePackage"
                          value={formDetails.servicePackage}
                          onChange={(e) => onFormUpdate('servicePackage', e.target.value)}
                          aria-required="false"
                          disabled={isFormDisabled}
                          className={`service-package-select form-input ${formState}`}
                        >
                          <option value="">Select one (optional)</option>
                          <option value="Starter">Rebel Starter - from $1,800 setup + $350/mo</option>
                          <option value="Basic">Rebel Basic - from $3,500 setup + $550/mo</option>
                          <option value="Intermediate">Rebel Intermediate - from $6,000 setup + $900/mo</option>
                          <option value="Pro">Rebel Pro - from $10,000 setup + $1,500/mo</option>
                          <option value="Custom">Custom Package</option>
                          <option value="Just Inquiring">Just Inquiring</option>
                        </select>
                      </Col>
                      <Col xs={12} className="px-1">
                        <label htmlFor="message" className="form-label">
                          Message<span aria-hidden="true" className="required-marker">*</span>
                        </label>
                        <textarea
                          id="message"
                          rows="6"
                          value={formDetails.message}
                          onChange={(e) => onFormUpdate('message', e.target.value)}
                          aria-required="true"
                          aria-invalid={!!fieldErrors.message}
                          aria-describedby={fieldErrors.message ? 'message-error' : undefined}
                          required
                          disabled={isFormDisabled}
                          className={`form-input ${formState} ${fieldErrors.message ? 'invalid' : ''}`}
                        />
                        {fieldErrors.message && (
                          <span id="message-error" role="alert" className="field-error">
                            {fieldErrors.message}
                          </span>
                        )}
                        
                        <button 
                          type="submit"
                          aria-label={
                            formState === 'loading' ? 'Sending message, please wait' : 
                            formState === 'success' ? 'Message sent successfully' :
                            'Send message'
                          }
                          disabled={isFormDisabled || isSubmitting}
                          className={`form-submit-button ${formState}`}
                        >
                          {getButtonContent()}
                        </button>
                      </Col>
                      
                      {/* Enhanced Status Display */}
                      {statusMessage && (
                        <Col xs={12} className="px-1">
                          <div 
                            role="alert"
                            aria-live="polite"
                            className="form-status-wrapper"
                          >
                            {getStatusContent()}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </form>
                </div>
          </Col>
        </Row>
      </Container>
    </section>
  )
}
